import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface QuickOption {
  label: string;
  value: string;
}

interface BuildSelections {
  serviceType?: string;
  budget?: string;
  timeline?: string;
  features?: string[];
}

const INITIAL_MESSAGE = "Hey! What can I help you build today?";

const INITIAL_OPTIONS: QuickOption[] = [
  { label: "AI Chatbot", value: "chatbot" },
  { label: "Automation", value: "automation" },
  { label: "Custom AI App", value: "custom" },
  { label: "Just browsing", value: "browsing" },
];

const BUDGET_OPTIONS: QuickOption[] = [
  { label: "Under $5K", value: "under-5k" },
  { label: "$5K - $15K", value: "5k-15k" },
  { label: "$15K - $50K", value: "15k-50k" },
  { label: "Not sure yet", value: "unsure" },
];

const TIMELINE_OPTIONS: QuickOption[] = [
  { label: "ASAP", value: "asap" },
  { label: "1-2 weeks", value: "1-2-weeks" },
  { label: "1 month+", value: "1-month-plus" },
  { label: "Just exploring", value: "exploring" },
];

const FEATURE_OPTIONS: QuickOption[] = [
  { label: "Voice support", value: "voice" },
  { label: "Multi-language", value: "multilang" },
  { label: "API integrations", value: "api" },
  { label: "Analytics", value: "analytics" },
];

type ConversationStage = "initial" | "budget" | "timeline" | "features" | "done" | "freeform";

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: INITIAL_MESSAGE }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [stage, setStage] = useState<ConversationStage>("initial");
  const [selections, setSelections] = useState<BuildSelections>({});
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  const getCurrentOptions = (): QuickOption[] => {
    switch (stage) {
      case "initial": return INITIAL_OPTIONS;
      case "budget": return BUDGET_OPTIONS;
      case "timeline": return TIMELINE_OPTIONS;
      case "features": return FEATURE_OPTIONS;
      default: return [];
    }
  };

  const getNextMessage = (selection: string, currentStage: ConversationStage): string => {
    switch (currentStage) {
      case "initial":
        if (selection === "browsing") return "No problem! Feel free to ask anything.";
        return "Got it! What's your budget range?";
      case "budget":
        return "When do you need this done?";
      case "timeline":
        return "Any special features? (Pick any that apply, then type 'done')";
      case "features":
        return "Thanks! Ready to get a quote?";
      default:
        return "";
    }
  };

  const getNextStage = (currentStage: ConversationStage, selection: string): ConversationStage => {
    if (currentStage === "initial" && selection === "browsing") return "freeform";
    switch (currentStage) {
      case "initial": return "budget";
      case "budget": return "timeline";
      case "timeline": return "features";
      case "features": return "done";
      default: return "freeform";
    }
  };

  const handleQuickOption = async (option: QuickOption) => {
    const userMsg: Message = { role: "user", content: option.label };
    setMessages(prev => [...prev, userMsg]);

    if (stage === "features") {
      if (selectedFeatures.includes(option.value)) {
        setSelectedFeatures(prev => prev.filter(f => f !== option.value));
      } else {
        setSelectedFeatures(prev => [...prev, option.value]);
      }
      return;
    }

    const newSelections = { ...selections };
    switch (stage) {
      case "initial":
        newSelections.serviceType = option.value;
        break;
      case "budget":
        newSelections.budget = option.value;
        break;
      case "timeline":
        newSelections.timeline = option.value;
        break;
    }
    setSelections(newSelections);

    setIsTyping(true);
    await new Promise(r => setTimeout(r, 500));

    const nextMsg = getNextMessage(option.value, stage);
    const nextStage = getNextStage(stage, option.value);

    setMessages(prev => [...prev, { role: "assistant", content: nextMsg }]);
    setStage(nextStage);
    setIsTyping(false);
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMsg: Message = { role: "user", content: input };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    setInput("");
    setIsTyping(true);

    if (stage === "features" && currentInput.toLowerCase() === "done") {
      const finalSelections = { ...selections, features: selectedFeatures };
      setSelections(finalSelections);
      
      await new Promise(r => setTimeout(r, 500));
      
      const summary = `Perfect! Here's what I got:
• Type: ${finalSelections.serviceType || "Custom"}
• Budget: ${finalSelections.budget || "TBD"}
• Timeline: ${finalSelections.timeline || "Flexible"}
• Features: ${selectedFeatures.length > 0 ? selectedFeatures.join(", ") : "Standard"}

Ready to see your quote? Head to /build or ask me anything!`;
      
      setMessages(prev => [...prev, { role: "assistant", content: summary }]);
      setStage("done");
      setIsTyping(false);
      return;
    }

    try {
      const contextMessage = Object.keys(selections).length > 0 
        ? `[Context: User wants ${selections.serviceType || "custom solution"}, budget ${selections.budget || "TBD"}, timeline ${selections.timeline || "TBD"}]\n\n${currentInput}`
        : currentInput;

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          messages: [...messages, { role: "user", content: contextMessage }]
        }),
      });

      const data = await response.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Connection issue. Try again?" 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const currentOptions = getCurrentOptions();
  const showOptions = currentOptions.length > 0 && !isTyping;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed bottom-20 right-6 w-[350px] z-50 shadow-2xl"
          >
            <Card className="border-primary/20 bg-card/95 backdrop-blur-md shadow-[0_0_30px_rgba(0,0,0,0.5)]">
              <CardHeader className="bg-primary/10 border-b border-primary/10 p-4 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-bold">AI Assistant</CardTitle>
                    <div className="text-xs text-green-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      Online
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)} data-testid="button-close-chat">
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-4 h-[350px] overflow-y-auto space-y-3" ref={scrollRef}>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    data-testid={`message-${msg.role}-${idx}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-secondary rounded-2xl px-4 py-3 flex gap-1" data-testid="status-typing">
                      <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" />
                      <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce delay-75" />
                      <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce delay-150" />
                    </div>
                  </div>
                )}
                
                {showOptions && (
                  <div className="flex flex-wrap gap-2 pt-2" data-testid="quick-options">
                    {currentOptions.map((opt) => (
                      <Button
                        key={opt.value}
                        variant={stage === "features" && selectedFeatures.includes(opt.value) ? "default" : "outline"}
                        size="sm"
                        className="text-xs rounded-full"
                        onClick={() => handleQuickOption(opt)}
                        data-testid={`option-${opt.value}`}
                      >
                        {opt.label}
                        {stage === "features" && selectedFeatures.includes(opt.value) && " ✓"}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter className="p-3 border-t border-white/5">
                <form 
                  className="flex w-full gap-2"
                  onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                >
                  <Input 
                    placeholder={stage === "features" ? "Type 'done' when ready..." : "Type a message..."} 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1 bg-background/50 border-white/10 focus-visible:ring-primary text-sm"
                    data-testid="input-chat"
                  />
                  <Button type="submit" size="icon" className="bg-primary hover:bg-primary/90" data-testid="button-send-chat">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </CardFooter>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-[0_0_20px_rgba(124,58,237,0.5)] flex items-center justify-center z-50 hover:bg-primary/90 transition-colors"
        data-testid="button-toggle-chat"
      >
        {isOpen ? <X /> : <MessageCircle />}
      </motion.button>
    </>
  );
}
